/**
 * Pure rules behind the Arya chat panel, kept free of Angular so they can be tested
 * on their own. The component only wires them to the screen.
 */

/** Poll fast while a turn is young, then ease off: long turns do not need 40 calls a minute. */
export const POLL_FAST_MS = 1500;
export const POLL_SLOW_MS = 4000;
export const POLL_FAST_FOR_MS = 60 * 1000;
/** After this long the UI stops watching and offers "Check again" instead of spinning. */
export const POLL_GIVE_UP_MS = 15 * 60 * 1000;
/** Consecutive failed polls before the UI says it lost the connection. */
export const MAX_POLL_FAILURES = 5;
/** How close to the bottom (px) still counts as "reading the latest message". */
export const STICK_TO_BOTTOM_PX = 80;

export function pollDelayMs(elapsedMs: number): number {
  return elapsedMs < POLL_FAST_FOR_MS ? POLL_FAST_MS : POLL_SLOW_MS;
}

export function pollExpired(elapsedMs: number): boolean {
  return elapsedMs > POLL_GIVE_UP_MS;
}

export interface ScrollBox {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}

/** True when the reader is at (or near) the newest message. If they scrolled up to read
 *  older ones, new data must not drag them back down. */
export function nearBottom(box: ScrollBox, threshold = STICK_TO_BOTTOM_PX): boolean {
  return box.scrollHeight - box.scrollTop - box.clientHeight <= threshold;
}

/** The panel belongs to a project. A refreshed copy of the SAME project (the page reloads
 *  it after edits) must not wipe the chat; only switching project starts a new one. */
export function projectChanged(previous: any, current: any): boolean {
  const before = previous && previous.project_id;
  const after = current && current.project_id;
  return !!before && !!after && before !== after;
}

/** Which chat to open when the panel loads: the one the owner was last in (if it still
 *  exists), otherwise the newest. */
export function pickThread(threads: { thread_id: string }[], remembered: string): string {
  if (!threads || !threads.length) {
    return remembered || '';
  }
  const known = threads.find(t => t.thread_id === remembered);
  return known ? known.thread_id : threads[0].thread_id;
}

/** Whether the "working" indicator should show. A turn that is being watched is busy;
 *  one we stopped watching (timed out or lost connection) must not trap the composer. */
export function isBusy(sending: boolean, status: string, stalled: boolean, connectionLost: boolean): boolean {
  if (stalled || connectionLost) {
    return false;
  }
  return sending || status === 'running';
}
